<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <CardComponent>
        <EventHeader :event="event" />
        <div>
          <span class="title">Manage Divisions</span>
          <b-button
            type="is-primary"
            class="is-pulled-right"
            @click="generateDivisions"
            >Generate Divisions</b-button
          >
          <b-tabs v-model="activeTab">
            <b-tab-item
              v-for="entrytype in event.entrytypes"
              :key="entrytype"
              :label="entrytype"
            >
              <b-collapse
                v-for="division of filteredDivisions"
                :key="division._id"
                class="card"
                animation="slide"
                :open="isOpen[division._id] === true"
                @open="isOpen[division._id] = true"
              >
                <div
                  slot="trigger"
                  slot-scope="props"
                  class="card-header"
                  style="background: #eee"
                  role="button"
                >
                  <p class="card-header-title">
                    {{ division.category }}
                    {{ division.gender ? ` - ${division.gender}` : '' }} ({{
                      division.workouts ? division.workouts.length : 0
                    }}
                    workouts)
                  </p>
                  <a class="card-header-icon">
                    <b-icon :icon="!props.open ? 'menu-down' : 'menu-up'">
                    </b-icon>
                  </a>
                </div>
                <div class="card-content">
                  <div class="content">
                    <b-dropdown aria-role="list">
                      <button
                        slot="trigger"
                        slot-scope="{ active }"
                        class="button"
                      >
                        <span>Manage</span>
                        <b-icon
                          :icon="active ? 'menu-up' : 'menu-down'"
                        ></b-icon>
                      </button>

                      <b-dropdown-item aria-role="listitem" has-link>
                        <router-link
                          :to="{
                            name: 'EditDivision',
                            params: { eventid: event._id, id: division._id },
                          }"
                          >Edit</router-link
                        >
                      </b-dropdown-item>
                      <b-dropdown-item aria-role="listitem" has-link>
                        <router-link
                          :to="{
                            name: 'CreateWorkout',
                            params: {
                              eventid: event._id,
                              divisionid: division._id,
                            },
                          }"
                          >Add Workout</router-link
                        >
                      </b-dropdown-item>
                      <b-dropdown-item aria-role="listitem" has-link>
                        <a href="" @click.prevent="promptCopyWorkouts(division)"
                          >Copy from other division</a
                        >
                      </b-dropdown-item>
                      <b-dropdown-item aria-role="listitem" has-link>
                        <a href="" @click.prevent="deleteDivision(division)"
                          >Delete</a
                        >
                      </b-dropdown-item>
                      <b-dropdown-item aria-role="listitem" has-link>
                        <a href="" @click.prevent="exportLeaderboard(division)"
                          >Export Leaderboard</a
                        >
                      </b-dropdown-item>
                    </b-dropdown>

                    <div class="has-margin-top-15">
                      <div class="has-text-weight-bold has-margin-bottom-15">
                        Workouts
                      </div>
                      <Draggable
                        v-model="division.workouts"
                        :group="division._id"
                        handle=".handle"
                        @start="drag = true"
                        @end="updateDivision(division)"
                      >
                        <transition-group>
                          <Workout
                            v-for="workout in division.workouts"
                            :key="workout._id"
                            :event="event"
                            :division="division"
                            :workout="workout"
                          />
                        </transition-group>
                      </Draggable>
                    </div>
                  </div>
                </div>
              </b-collapse>
            </b-tab-item>
          </b-tabs>
        </div>
      </CardComponent>
    </section>
    <b-modal :active.sync="showCopyModal">
      <div class="card">
        <header class="modal-card-head">
          <p class="modal-card-title">Copy workouts from other division</p>
        </header>
        <div class="card-content">
          <b-field label="Choose division to copy from">
            <b-select v-model="divisionToCopy">
              <option
                v-for="division in divisionsToCopy"
                :key="division._id"
                :value="division"
              >
                {{ division.entrytype }} - {{ division.category }}
                {{
                  division.entrytype === 'Individual'
                    ? `- ${division.gender}`
                    : ''
                }}
              </option>
            </b-select>
          </b-field>
        </div>
        <footer class="modal-card-foot">
          <b-button @click="showCopyModal = false">Cancel</b-button>
          <b-button
            type="is-primary"
            :disabled="!divisionToCopy"
            @click="copyWorkouts"
            >Copy</b-button
          >
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import EventHeader from '@/components/EventHeader'
import CardComponent from '@/components/CardComponent'
import Workout from '@/components/Workout'
import EventWorkoutApi from '@/api/event-workout'
import LeaderboardApi from '@/api/leaderboard'
import Draggable from 'vuedraggable'

export default {
  name: 'ManageDivisions',
  components: {
    CardComponent,
    EventHeader,
    Workout,
    Draggable,
  },
  data() {
    return {
      activeTab: 0,
      entries: [],
      adding: false,
      initialized: false,
      isOpen: {},
      showCopyModal: false,
      divisionToCopy: null,
      selectedDivision: {},
    }
  },
  computed: {
    ...mapGetters(['event']),
    selectedEntryType() {
      return this.event.entrytypes[this.activeTab]
    },
    filteredDivisions() {
      return this.entries.filter(
        (entry) => entry.entrytype === this.selectedEntryType
      )
    },
    divisionsToCopy() {
      return this.entries.filter(
        (entry) => entry._id !== this.selectedDivision._id
      )
    },
  },
  async created() {
    this.entries = await EventWorkoutApi.divisionsForEvent(
      this.$route.params.eventid
    )
    this.entries.forEach((division) => {
      this.isOpen[division._id] = false
    })
    this.initialized = true
  },
  methods: {
    promptCopyWorkouts(division) {
      this.selectedDivision = division
      this.showCopyModal = true
    },
    async copyWorkouts() {
      if (
        this.divisionToCopy.workouts &&
        this.divisionToCopy.workouts.length > 0
      ) {
        this.divisionToCopy.workouts.forEach((workout) => {
          let found = this.selectedDivision.workouts.find(
            (item) => item.name === workout.name
          )
          if (!found) {
            this.selectedDivision.workouts.push(workout)
          }
        })
      }
      this.$buefy.toast.open({
        message: 'Workouts copied successfully.',
        position: 'is-top',
        type: 'is-success',
      })
      await EventWorkoutApi.updateDivision(this.selectedDivision)
      this.entries = await EventWorkoutApi.divisionsForEvent(
        this.$route.params.eventid
      )
      this.showCopyModal = false
    },
    async generateDivisions() {
      await EventWorkoutApi.generateDivisions(this.$route.params.eventid)
      this.entries = await EventWorkoutApi.divisionsForEvent(
        this.$route.params.eventid
      )
    },
    deleteDivision(entry) {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this division?',
        onConfirm: async () => {
          await EventWorkoutApi.removeDivision(entry._id)
          this.entries = this.entries.filter((item) => item._id !== entry._id)
          this.$buefy.toast.open({
            message: 'Division deleted.',
            position: 'is-top',
            type: 'is-success',
          })
        },
      })
    },
    async exportLeaderboard(division) {
      await LeaderboardApi.export({
        event: this.$route.params.eventid,
        entrytype: division.entrytype,
        category: division.category,
        gender: division.gender,
      })
    },
    async updateDivision(division) {
      await EventWorkoutApi.updateDivision(division)
    },
  },
}
</script>
